/*
 *
 * ShopSelector
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 05-May-2025
 * ========================================
 * 💡 COMPONENT OVERVIEW
 *
 * ShopSelector is used to show a list of "Parent" entities (e.g. payment groups or locations),
 * each containing a list of "Child" elements (e.g. payment contracts).
 *
 * The user can:
 * - select/deselect individual shops
 * - select/deselect all shops of a parent
 * - use "Select all" or "Clear all" for the full group
 * - filter content using a search bar
 *
 * It works together with ShopPickerPopup.
 *
 * ⚠️ The selection state is local and synchronized upward via `onChange`.
 * ⚠️ It also supports pre-populating the state via `selections` or fallback `shopSaved`.
 *
 */

import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import { ChevronDown } from '@nexidigital/iconlibrary'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import PortalSearchBar from '@nexidigital/portalsearchbar'
import MerchantTableBadge from '@nexidigital/merchanttablebadge'
import PortalIconButton from '@nexidigital/portaliconbutton'
import PortalLoader from '@nexidigital/portalloader'
import PortalButton from '@nexidigital/portalbutton'
import { capitalizeFirstLetter, nullSafe, nullSafeString } from 'Utility/globalMethods'
import { mergeSelectionWithParents, mergeSelections } from 'Utility/shopListNormalizer'
import { flowToastMapType } from 'Utility/flowToastMap'
import formattedMessage from './languageModule'
import './ShopSelector.scss'
import { useIntl } from 'react-intl'

const MAX_AUTO_EXPANDED_PARENTS = 3

const ShopSelector = ({ data, onChange, tabSelected, clearAction, actions, shopListLoading, shopSearchTerm, shopLocalSelection, noParentRowBorder, error, inPageFlow, autoInitializeSmallGroups }) => {
  const { multiPiva, title, label, code, group, initialParents, selections, maxSelectableShops, externalSelectedCount = 0 } = data

  const [parentsState, setParentsState] = useState([])
  const [visibleChildren, setVisibleChildren] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [searchedValue, setSearchedValue] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [selectAllError, setSelectAllError] = useState(false)

  const didInit = useRef(false)
  const initialExpansionInitialized = useRef(false)
  const searchBarContainerRef = useRef(null)

  const intl = useIntl()

  // ShopSelector is always in checkbox mode
  const hasCheckbox = true

  function debounce (func, delay) {
    let timeout
    return (...args) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), delay)
    }
  }

  // Semplice debounce che si affida all'AbortController dell'API per gestire la cancellazione
  const debouncedSearch = useRef(
    debounce((value) => {
      const trimmed = value.trim()

      if (trimmed === '') {
        setSearchedValue(false)
        setIsLoading(true)
        // Reset: chiama API senza filtro per ripristinare la lista completa
        actions?.doGetShopList({ filter: '', currentGroup: originalGroup })
      } else if (trimmed.length >= 3) {
        setSearchedValue(true)
        setIsLoading(true)
        // L'AbortController nell'API si occupa di cancellare le richieste precedenti
        actions?.doGetShopList({ filter: trimmed, currentGroup: originalGroup })
      }
    }, 300)
  ).current

  useEffect(() => {
    if (didInit.current) return
    didInit.current = true

    if (typeof shopSearchTerm === 'string') {
      setSearchTerm(shopSearchTerm)

      if (shopSearchTerm.trim().length >= 3) {
        setSearchedValue(true)
      }
    }

    const sourceSelections =
      selections?.length > 0
        ? selections
        : shopLocalSelection?.[group] || []

    const merged = mergeSelectionWithParents(initialParents, sourceSelections)

    setParentsState(JSON.parse(JSON.stringify(merged)))
  }, [])

  useEffect(() => {
    if (!initialParents) return

    const fromLocal = selections?.length > 0
      ? selections
      : shopLocalSelection?.[group] || []
    const merged = mergeSelectionWithParents(initialParents, fromLocal)
    setParentsState(JSON.parse(JSON.stringify(merged)))

    if (!initialExpansionInitialized.current && merged.length > 0) {
      setVisibleChildren(
        autoInitializeSmallGroups && merged.length <= MAX_AUTO_EXPANDED_PARENTS
          ? merged.reduce((visibleChildren, parent, index) => {
            if (parent.shops?.length > 0) visibleChildren[index] = true
            return visibleChildren
          }, {})
          : {}
      )
      initialExpansionInitialized.current = true
    }

    // Reset loader quando i dati arrivano
    setIsLoading(false)

    // Sincronizza shopLocalSelection con gli elementi selezionati che vengono da initialParents
    // Questo è necessario per non perdere selezioni pre-esistenti dal backend
    const selectedFromMerge = merged
      .filter(p => p.isSelected || p.shops?.some(s => s.isSelected))
      .map(p => ({
        parent: {
          title: p.title,
          label: p.label,
          code: p.code,
          address: nullSafeString(() => p.address, ''),
          addressObj: nullSafe(() => p.addressObj, {}),
          labelSelection: p.labelSelection,
          shopId: p.shopId,
          type: p.type,
          isSelected: p.isSelected
        },
        shops: (p.shops || [])
          .filter(s => s.isSelected)
          .map(s => ({ code: s.code, isSelected: s.isSelected, shopId: s.shopId }))
      }))

    // Merge le selezioni dal merge con quelle esistenti in shopLocalSelection
    const syncedSelections = mergeSelections(fromLocal, selectedFromMerge)

    // Aggiorna solo se ci sono differenze
    if (JSON.stringify(syncedSelections) !== JSON.stringify(fromLocal)) {
      actions.doSetShopLocalSelection({ group, data: syncedSelections })
    }

    // Ripristina il focus sulla search bar se c'è una ricerca attiva
    // Questo previene la perdita di focus durante la digitazione quando i risultati vengono aggiornati
    // Usa shopSearchTerm invece di searchTerm perché searchTerm potrebbe essere resettato prima che questo useEffect venga eseguito
    if (shopSearchTerm && searchBarContainerRef.current) {
      // Usa setTimeout per assicurarsi che il DOM sia stato aggiornato
      setTimeout(() => {
        const input = searchBarContainerRef.current?.querySelector('input')

        if (input && document.activeElement !== input) {
          input.focus()
          // Ripristina la posizione del cursore alla fine del testo
          const length = input.value.length
          input.setSelectionRange(length, length)
        }
      }, 0)
    }
  }, [initialParents, shopSearchTerm])

  const normalizedToOriginalGroupMap = useMemo(() => {
    const map = {
      NEXI: 'NEXI',
      'INTESA SANPAOLO': 'INTESA_SANPAOLO',
      'NEXI DIRECT': 'NEXI_DIRECT',
      OTHER: 'OTHER'
    }

    return map
  }, [])

  const originalGroup = normalizedToOriginalGroupMap[group] || group

  // Conta gli shop selezionati in un newState (parent senza children = 1, altrimenti somma children selezionati)
  const countSelectedInState = (state) =>
    state.reduce(
      (acc, p) =>
        acc + (p.shops?.length > 0
          ? p.shops.filter((c) => c.isSelected).length
          : p.isSelected ? 1 : 0),
      0
    )

  // Accepts a flag to indicate if the change was triggered by selectAll
  const handleChange = (newState, { triggeredBySelectAll = false } = {}) => {
    // Controllo limite massimo selezionabile (cross-gruppo se externalSelectedCount viene passato)
    if (typeof maxSelectableShops === 'number' && maxSelectableShops > 0) {
      const newGroupCount = countSelectedInState(newState)
      const totalAfterChange = newGroupCount + externalSelectedCount

      if (totalAfterChange > maxSelectableShops) {
        actions?.doShowToast?.(flowToastMapType.ERROR_TOAST, {
          label: formattedMessage.maxSelectableExceeded(maxSelectableShops)
        })
        if (triggeredBySelectAll) setSelectAllError(true)
        return
      }
    }

    setSelectAllError(false)

    const isOtherGroup = tabSelected !== group
    const hasAnySelection = newState.some(p =>
      p.isSelected || p.shops?.some(c => c.isSelected)
    )

    // Only clear other groups if we're not in DEFAULT group mode
    if (isOtherGroup && hasAnySelection && group !== 'DEFAULT') {
      clearAction?.(group)
    }

    setParentsState(newState)

    const updated = newState
      .map((p) => {
        const parentBase = {
          title: p.title,
          label: p.label,
          code: p.code,
          address: nullSafeString(() => p.address, ''),
          addressObj: nullSafe(() => p.addressObj, {}),
          labelSelection: p.labelSelection,
          shopId: p.shopId,
          type: p.type
        }

        return {
          parent: hasCheckbox
            ? {
                ...parentBase,
                isSelected: (p.shops.length === 0 || p.hideChildsCheckbox) ? p.isSelected : undefined
              }
            : {
                ...parentBase,
                isSelected: p.isSelected
              },
          shops: (p.shops || []).map((c) => ({
            code: c.code,
            isSelected: c.isSelected,
            shopId: c.shopId
          }))
        }
      })
      .filter((entry) =>
        hasCheckbox ? entry.shops.length > 0 || typeof entry.parent.isSelected === 'boolean' : entry.parent.isSelected
      )

    // Merge con le selezioni precedenti per non perdere gli elementi selezionati
    // che non sono visibili nei risultati della ricerca corrente
    const previousSelections = shopLocalSelection?.[group] || []
    const mergedSelections = mergeSelections(previousSelections, updated)

    actions.doSetShopLocalSelection({ group, data: mergedSelections })

    onChange({ group, updated: mergedSelections })
  }

  const handleSelectAll = () => {
    const allFilteredSelected = filteredParents.every((p) =>
      p.shops.length > 0 ? p.shops.every((c) => c.isSelected) : p.isSelected
    )

    const updated = parentsState.map((p) => {
      const isFiltered = filteredParents.find((fp) => fp.code === p.code)
      if (!isFiltered) return p

      if (p.shops.length > 0) {
        return {
          ...p,
          isSelected: !allFilteredSelected,
          shops: p.shops.map((c) => ({
            ...c,
            isSelected: !allFilteredSelected
          }))
        }
      } else {
        return {
          ...p,
          isSelected: !allFilteredSelected
        }
      }
    })

    handleChange(updated, { triggeredBySelectAll: true })
  }

  const handleClearAll = () => {
    const updated = parentsState.map((p) => {
      const isFiltered = filteredParents.find((fp) => fp.code === p.code)
      if (!isFiltered) return p

      return {
        ...p,
        isSelected: p.shops.length === 0 ? false : p.isSelected,
        shops: p.shops.map((c) => ({
          ...c,
          isSelected: false
        }))
      }
    })

    handleChange(updated)
  }

  const handleParentToggle = (i) => {
    const hasChildren = parentsState[i].shops.length > 0
    if (hasChildren) {
      const allSelected = parentsState[i].shops.every((c) => c.isSelected)
      const updated = [...parentsState]
      updated[i] = {
        ...updated[i],
        shops: updated[i].shops.map((c) => ({ ...c, isSelected: !allSelected })),
        isSelected: !allSelected
      }
      handleChange(updated)
    } else {
      const updated = [...parentsState]
      updated[i] = { ...updated[i], isSelected: !updated[i].isSelected }
      handleChange(updated)
    }
  }

  const handleChildToggle = (pi, ci) => {
    const shop = {
      ...parentsState[pi].shops[ci],
      isSelected: !parentsState[pi].shops[ci].isSelected
    }
    delete shop.selected
    const updated = parentsState.map((p, idx) => {
      if (idx !== pi) return p
      return { ...p, shops: p.shops.map((s, sidx) => sidx === ci ? shop : s) }
    })
    handleChange(updated)
  }

  const handleChangeSearch = (value) => {
    setSearchTerm(value)
    actions?.doSetShopSearchTerm?.(value)

    // Mostra loader immediatamente quando l'utente digita 3+ caratteri o cancella tutto
    const trimmed = value.trim()
    if (trimmed.length >= 3 || trimmed === '') {
      setIsLoading(true)
    } else {
      // Se l'utente cancella a 1-2 caratteri, ferma il loader (nessuna chiamata API)
      setIsLoading(false)
    }

    // Usa sempre il debounce - l'AbortController nell'API gestisce le race conditions
    debouncedSearch(value)
  }

  const handleSearch = () => {
    const trimmed = searchTerm.trim()
    if (trimmed.length < 3 && trimmed !== '') return

    if (trimmed === '') {
      setSearchedValue(false)
    } else {
      setSearchedValue(true)
    }

    actions?.doGetShopList({ filter: trimmed, currentGroup: originalGroup })
  }

  const filteredParents = parentsState

  const allSelected =
    parentsState.length > 0 &&
    parentsState.every((p) => (p.shops.length > 0 ? p.shops.every((c) => c.isSelected) : p.isSelected))

  const someSelected = parentsState.some((p) =>
    p.shops.length > 0 ? p.shops.some((c) => c.isSelected) : p.isSelected
  )

  const selectedCount = parentsState.reduce(
    (acc, p) => acc + (p.shops.length > 0 ? p.shops.filter((c) => c.isSelected).length : p.isSelected ? 1 : 0),
    0
  )

  return (
    <Toggle>
      <div className={`shopSelector ${multiPiva ? 'multiPiva' : ''} ${inPageFlow ? 'inPageFlow' : ''}`}>
        {multiPiva && (
          <div className='shopSelectorPiva'>
            <div className='shopSelectorPivaContainer'>
              <div className='shopSelectorPivaContent'>
                {title && <p className='pivaLabel'>{title}</p>}
                <div className='pivaRow'>
                  <p className='pivaCode'>
                    {label}: <div className='codeBox'>{code}</div>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='shopSelectorSearchBar' ref={searchBarContainerRef}>
          <PortalSearchBar
            placeholder={intl.formatMessage({ id: 't.app.shopSelector.searchBarLabel' })}
            cleanField
            value={searchTerm}
            onChange={(e) => {
              handleChangeSearch(e)
            }}
            onEnter={() => handleSearch()}
            onSearchClick={() => handleSearch()}
            liveSearch
          />
        </div>
        {shopSearchTerm?.trim().length >= 3 && !shopListLoading && !isLoading && (
          <div className='shopSelectorSearchResults'>
            <p>
              {formattedMessage.resultsFoundLabel}: <b>{filteredParents.length}</b>
            </p>
          </div>
        )}
        {(hasCheckbox && !shopListLoading && !isLoading && filteredParents.length > 0) && (
          <div className='shopSelectorChildrenCheckBox'>
            <div className='shopSelectorChildrenCheckBoxLeft'>
              <PortalCheckBox
                ariaLabel={searchedValue ? formattedMessage.selectAllResultsLabel : formattedMessage.selectAllLabel}
                options={[
                  {
                    id: 'selectAll',
                    label: (
                      <div>
                        <p className={`shopSelectorGlobalLabel ${someSelected ? 'selected' : ''}`}>
                          {searchedValue ? formattedMessage.selectAllResultsLabel : formattedMessage.selectAllLabel}
                        </p>
                        {selectedCount > 0 && (
                          <div className='shopSelectorGlobalSummary'>
                            <p>{selectedCount}</p>
                            {selectedCount === 1
                              ? <p>{formattedMessage.selectedLabelSingle}</p>
                              : <p>{formattedMessage.selectedLabel}</p>}
                          </div>
                        )}
                      </div>
                    ),
                    analyticsLabel: searchedValue ? { id: 't.app.shopSelector.selectAllResultsLabel', defaultMessage: 'Seleziona tutti i risultati' } : { id: 't.app.shopSelector.selectAllLabel', defaultMessage: 'Seleziona tutto' },
                    checked: allSelected,
                    indeterminate: someSelected && !allSelected,
                    disabled: filteredParents.length === 0,
                    error: selectAllError
                  }
                ]}
                onChange={({ id }) => id === 'selectAll' && handleSelectAll()}
              />
            </div>
            {someSelected && <PortalButton size='small' textButton label={formattedMessage.clearLabel} onClick={handleClearAll} />}
          </div>
        )}

        {(shopListLoading || isLoading)
          ? (
            <div className='shopPickerLoading'>
              <PortalLoader circular centered />
            </div>
            )
          : (
            <div className={`shopSelectorScroller ${filteredParents.length === 0 ? 'empty' : ''}`}>
              <div className='shopSelectorParent'>
                {(
                  filteredParents.map((parent, pi) => {
                    const hasChildren = (parent.shops || []).length > 0
                    const selected = hasChildren
                      ? parent.shops.filter((c) => c.isSelected).length
                      : parent.isSelected
                        ? 1
                        : 0
                    const childrenVisible = visibleChildren[pi]

                    return (
                      <div key={parent.code} className={`shopSelectorParentRow ${selected > 0 ? 'selection' : ''} ${!noParentRowBorder ? 'parentRowBorder' : ''}`}>
                        <div
                          className={`shopSelectorContainer ${childrenVisible ? 'open' : ''}`}
                          onClick={() => handleParentToggle(pi)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleParentToggle(pi)
                            }
                          }}
                          role='button'
                          tabIndex={0}
                        >
                          <div className='shopSelectorCheckBox'>
                            <div
                              className='shopSelectorParentCheckboxWrapper'
                              role='none'
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PortalCheckBox
                                tabIndex={-1}
                                ariaLabel={`Shop: ${parent.title || parent.code}`}
                                onChange={() => handleParentToggle(pi)}
                                options={[
                                  {
                                    label: `Shop: ${parent.title || parent.code}`,
                                    analyticsLabel: capitalizeFirstLetter(parent.title || parent.code),
                                    checked: hasChildren ? parent.shops.every((c) => c.isSelected) : parent.isSelected,
                                    error,
                                    indeterminate:
                                      hasChildren &&
                                      parent.shops.some((c) => c.isSelected) &&
                                      !parent.shops.every((c) => c.isSelected)
                                  }
                                ]}
                              />
                            </div>
                          </div>

                          <div className='shopSelectorParentContent checkbox'>
                            <div className='parentRow'>
                              {parent.title && <p className='parentLabel'>{capitalizeFirstLetter(parent.title)}</p>}
                              {parent.badgeTitleLabel && (<div className='parentBadgeLabel'><p className='parentLabel'>{parent.badgeTitleLabel}</p> <MerchantTableBadge label={parent.code} status='neutralLight' /> </div>)}
                              <div className='codeLabelsContainer'>
                                {
                                  !parent.hideCode && (
                                    <p className='code'>
                                      {parent.primaryCodeLabel || parent.codeLabel || 'Code'}: <span className='codeBox'>{parent.primaryCode || parent.code}</span>
                                    </p>
                                  )
                                }
                                {parent.secondaryCodeLabel && (
                                  <p className='code'>
                                    {parent.secondaryCodeLabel}: <span className='codeBox'>{parent.secondaryCode || parent.code}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className='parentRow lessSpace'>
                              {nullSafeString(() => parent.address, '') && (<p className='address'>{nullSafeString(() => parent.address, '')}</p>)}
                              {hasChildren && (
                                <p className='selection'>
                                  {parent.customSelectionText
                                    ? (
                                      <>
                                        <b>{selected}</b> {parent.labelSelection || formattedMessage.selectedLabel}
                                      </>
                                      )
                                    : (
                                      <>
                                        <b>{selected}</b> {formattedMessage.ofLabel} <b>{parent.shops.length}</b>{' '}
                                        {parent.labelSelection || formattedMessage.selectedLabel}
                                      </>
                                      )}
                                </p>
                              )}
                              {parent.typologyLabel && (
                                <p className='typology'>
                                  {formattedMessage.typology}:
                                  <MerchantTableBadge symbol label={parent.typologyLabel} status='brand' />
                                </p>
                              )}
                            </div>
                          </div>

                          {hasChildren && (
                            /* eslint-disable */
                            <div
                              className={`shopSelectorIcon ${childrenVisible ? 'open' : ''}`}
                              role='button'
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setVisibleChildren((prev) => ({ ...prev, [pi]: !prev[pi] }))
                                }
                              }}
                              aria-label={childrenVisible ? intl.formatMessage({ id: 't.app.shopSelector.collapseShops' }) : intl.formatMessage({ id: 't.app.shopSelector.expandShops' })}
                              aria-expanded={childrenVisible}
                            >
                              <PortalIconButton
                                icon={<ChevronDown size='24' type='line' />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setVisibleChildren((prev) => ({ ...prev, [pi]: !prev[pi] }))
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {childrenVisible &&
                          hasChildren &&
                          parent.shops.map((child, ci) => (
                            <div
                              key={child.code}
                              className={`shopSelectorChildrenContent ${child.isSelected ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleChildToggle(pi, ci)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleChildToggle(pi, ci)
                                }
                              }}
                              role='button'
                              tabIndex={0}
                            >
                              <div className='shopSelectorChildrenContainer'>
                                <div className='shopSelectorChildrenSelection'>
                                  {!parent.hideChildsCheckbox && (
                                    <div
                                      className='shopSelectorChildCheckboxWrapper'
                                      role='none'
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <PortalCheckBox
                                        tabIndex={-1}
                                        ariaLabel={`Shop: ${child.labelTop || child.code}`}
                                        onChange={() => handleChildToggle(pi, ci)}
                                        options={[
                                          {
                                            label: `Shop: ${child.labelTop || child.code}`,
                                            analyticsLabel: capitalizeFirstLetter(child.labelTop || child.code),
                                            checked: child.isSelected
                                          }
                                        ]}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className='shopSelectorChildrenContext'>
                                  <div className='childrenHeader'>
                                    <div className='childrenRow'>
                                      {child.labelTop && <p className='childrenLabel'>{capitalizeFirstLetter(child.labelTop)}</p>}
                                    </div>
                                    <div className='childrenRow'>
                                      <p className='codeChildren'>
                                        {child.childrenCodelabel}: <span className='codeBox'>{child.code}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <div className='childrenBottom'>
                                    <div className={`childrenRow ${child.address ? 'addressTypology' : ''}`}>
                                      <p className='address'>{nullSafeString(() => child.address, '')}</p>
                                      <p className='typology'>
                                        {formattedMessage.typology}:
                                        <MerchantTableBadge symbol label={child.childrenTypologyLabel} status='brand' small />
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}
      </div>
    </Toggle>
  )
}

ShopSelector.propTypes = {
  data: PropTypes.shape({
    multiPiva: PropTypes.bool,
    title: PropTypes.string,
    label: PropTypes.string,
    code: PropTypes.string,
    group: PropTypes.string,
    initialParents: PropTypes.array,
    selections: PropTypes.array,
    maxSelectableShops: PropTypes.number,
    externalSelectedCount: PropTypes.number
  }),
  onChange: PropTypes.func.isRequired,
  shopSaved: PropTypes.array,
  tabSelected: PropTypes.string,
  clearAction: PropTypes.func,
  actions: PropTypes.object,
  shopListLoading: PropTypes.bool,
  shopSearchTerm: PropTypes.string,
  shopLocalSelection: PropTypes.object,
  maxSelectableShops: PropTypes.number,
  externalSelectedCount: PropTypes.number,
  noParentRowBorder: PropTypes.bool,
  error: PropTypes.bool,
  // Indicates if the component is used within a page flow
  inPageFlow: PropTypes.bool,
  autoInitializeSmallGroups: PropTypes.bool
}

ShopSelector.defaultProps = {
  data: {
    multiPiva: false,
    title: '',
    label: '',
    code: '',
    group: '',
    initialParents: [],
    selections: []
  },
  shopSaved: [],
  tabSelected: '',
  clearAction: () => { },
  actions: {},
  shopListLoading: false,
  shopSearchTerm: '',
  shopLocalSelection: {},
  maxSelectableShops: 0,
  externalSelectedCount: 0,
  noParentRowBorder: false,
  error: false,
  inPageFlow: false,
  autoInitializeSmallGroups: false
}

export default ShopSelector
